import { recordRumError } from '@/rum';

import React, { useCallback, useEffect, useRef } from 'react';

import { Alert, Box, Button, Typography } from '@mui/material';
import {
  Background,
  BackgroundVariant,
  type Connection,
  Controls,
  type Edge,
  type EdgeChange,
  MiniMap,
  type Node,
  type NodeChange,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { debounce } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router';

import { logger } from '@/utils/logger';

import { SnackbarProvider, useSnackbar } from '../../components/feedback/SnackbarProvider';
import { useGetTransformerVersionsQuery } from '../transformers/api/transformersApi';
import { DnDProvider, useDnD } from './DnDContext';
import Sidebar from './Sidebar';
import { CustomAdvancedPanel } from './components/AdvancedControls';
import FallbackRender from './components/error-boundary-fallback';
import { Constraints } from './config/constraints';
import nodeTypesToComponentMapping from './config/node-type-to-component-map';
import { useRecipeEditor } from './context/RecipeEditorContext';
import { NodeConstraintsProvider } from './context/node-constraints-context';
import { NodeGenerationProvider, useNodeGeneration } from './context/node-generation-context';
import NodeNamesProvider from './context/node-names.context';
import { CustomReactFlowStateProvider, useCustomReactFlowState } from './context/reactflow-context';
import './flow.css';
import { useFlowChangeHandlers } from './hooks/useFlowChangeHandlers';
import { useFlowDragDrop } from './hooks/useFlowDragDrop';
import useFlowEvents from './hooks/useFlowEvents';
import { RecipeProvider, useRecipe } from './recipe-context';
import { isValidConnection } from './utils/isValidConnection';
import { updateRecipeFromNodesAndEdges } from './utils/updateRecipeFromNodesAndEdges';

// Wrapper for loading/error states to fill container
const StateWrapper = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      height: '100%',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      minHeight: 0,
      minWidth: 0,
    }}
  >
    {children}
  </Box>
);

export const Flow = () => {
  const { showSnackbar } = useSnackbar();
  const params = useParams();
  const { state, dispatch } = useRecipeEditor();

  // Route uses :recipeId not :transId
  const { appId } = params;
  const transId = params.recipeId; // Changed from transId to recipeId to match route

  // RTK Query hooks
  const {
    data: versions,
    isLoading: isLoadingVersions,
    error: versionsError,
  } = useGetTransformerVersionsQuery(
    { transBaseId: transId || '', appId: appId || '' },
    { skip: !transId || !appId }
  );

  // Log API errors for debugging
  useEffect(() => {
    if (versionsError) {
      logger.error('[Flow] Error fetching versions:', versionsError);
    }
  }, [versionsError]);

  const { updateIsReadOnly } = useCustomReactFlowState();
  const { recipe, updateRecipe } = useRecipe();
  const { applyNodesPositioning, generateNodesAndEdges } = useNodeGeneration();
  const { getNodes, getEdges, screenToFlowPosition } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [type, setType] = useDnD();

  // Use refs to track values without causing re-renders
  const lastLoadedTransId = useRef<string | null>(null);
  const lastLoadedVersion = useRef<string | null>(null);
  const isInitialLoad = useRef(true);
  const isLoading = useRef(false);
  const isLoadingRecipe = useRef(false); // Track if we're currently loading a recipe
  const {
    onReconnectStart,
    onConnectStart,
    onConnect: onConnectFlow,
    onReconnect,
    onConnectEnd,
    onReconnectEnd,
    onEdgesDelete,
  } = useFlowEvents(state.isReadOnly);

  const { isReadOnly } = state;
  const { error } = state;
  const { isSidebarCollapsed } = state;

  useEffect(() => {
    updateIsReadOnly(state.isReadOnly);
  }, [state.isReadOnly, updateIsReadOnly]);

  // Clear error state when recipe/version changes (but only on change, not when error is set)
  useEffect(() => {
    if (error && (transId || state.currentVersion)) {
      // Only clear if we're about to load new data
      dispatch({ type: 'SET_ERROR', payload: false });
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally omit 'error' and 'dispatch' to prevent immediate clear on error state changes
  }, [transId, state.currentVersion]);

  // Debounced function to update recipe JSON in sessionStorage
  // This builds the updated recipe for the save operation
  // NOTE: This does NOT touch isModified - that's purely event-driven now!
  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce() returns a stable reference, getNodes/getEdges from ReactFlow are stable
  const debouncedUpdateRecipe = useCallback(
    debounce(() => {
      const nodes = getNodes();
      const edges = getEdges();

      // Always use the original recipe from network to avoid accumulating duplicates
      const currentRecipeFromNetwork = JSON.parse(
        sessionStorage.getItem('CURRENT_RECIPE_FROM_NETWORK') || '{}'
      );

      const updatedRecipe = updateRecipeFromNodesAndEdges(nodes, edges, currentRecipeFromNetwork);
      sessionStorage.setItem('UPDATED_RECIPE', JSON.stringify(updatedRecipe));

      // Update flow save state for UI indicators (not used for save button anymore)
      dispatch({
        type: 'SET_FLOW_SAVE_STATE',
        payload: {
          isSaving: false,
          hasUnsavedChanges: true, // If we got here, user made changes
          isComparingRecipes: false,
        },
      });
    }, 400),
    [getNodes, getEdges, dispatch]
  );

  // Drag & drop handlers (extracted to custom hook)
  const { onDragOver, onDrop } = useFlowDragDrop({
    type,
    setType,
    setNodes,
    screenToFlowPosition,
    debouncedUpdateRecipe,
  });

  // Change handlers with event emission (extracted to custom hook)
  const changeHandlers = useFlowChangeHandlers({
    isReadOnly,
    onNodesChange,
    onEdgesChange,
    setEdges,
    onConnectFlow,
    debouncedUpdateRecipe,
  });

  // Wrap handlers to skip events during recipe loading
  const handleOnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Skip events if we're loading a recipe
      if (isLoadingRecipe.current) {
        onNodesChange(changes);
        return;
      }
      changeHandlers.handleOnNodesChange(changes);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- changeHandlers is a stable object from custom hook
    [changeHandlers.handleOnNodesChange, onNodesChange]
  );

  const handleOnEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Skip events if we're loading a recipe
      if (isLoadingRecipe.current) {
        onEdgesChange(changes);
        return;
      }
      changeHandlers.handleOnEdgesChange(changes);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- changeHandlers is a stable object from custom hook
    [changeHandlers.handleOnEdgesChange, onEdgesChange]
  );

  const handleOnConnect = useCallback(
    (params: Connection) => {
      // Skip events if we're loading a recipe
      if (isLoadingRecipe.current) {
        if (!isReadOnly) {
          onConnectFlow(params);
          setEdges((eds) => addEdge({ ...params, animated: true, type: 'smoothstep' }, eds));
        }
        return;
      }
      changeHandlers.handleOnConnect(params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- changeHandlers is a stable object from custom hook
    [changeHandlers.handleOnConnect, isReadOnly, onConnectFlow, setEdges]
  );

  const handlePrettify = () => {
    const currentNodes = getNodes();
    const currentEdges = getEdges();

    applyNodesPositioning(currentNodes, currentEdges)
      .then((result: { nodes: Node[]; edges: Edge[] }) => {
        if (result && result.nodes && result.edges) {
          setNodes([...result.nodes]);
          setEdges([...result.edges]);
          dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });
        }
      })
      .catch((error: unknown) => {
        logger.error('[handlePrettify] Error applying layout:', error);
      });
  };

  // Load recipe when versions data is available
  useEffect(() => {
    if (!versions || versions.length === 0 || isLoading.current) {
      return;
    }

    const shouldLoad = isInitialLoad.current || lastLoadedTransId.current !== transId;
    if (!shouldLoad) {
      return;
    }

    try {
      isLoading.current = true;
      isLoadingRecipe.current = true; // Set flag to prevent change events during load

      // Clear session storage when switching recipes
      if (lastLoadedTransId.current !== transId) {
        sessionStorage.removeItem('CURRENT_RECIPE_FROM_NETWORK');
        sessionStorage.removeItem('UPDATED_RECIPE');
      }

      // Find the requested version or use the latest
      // Sort versions - handle both string and number versions safely
      const sortedVersions = [...versions].sort((a, b) => {
        const versionA = String(a.version);
        const versionB = String(b.version);
        return versionB.localeCompare(versionA, undefined, { numeric: true, sensitivity: 'base' });
      });

      // Find version using string comparison to handle both string and number versions
      const targetVersion = state.currentVersion ? String(state.currentVersion) : null;
      const version = targetVersion
        ? versions.find((v) => String(v.version) === targetVersion) || sortedVersions[0]
        : sortedVersions[0];

      // Check for actual config data (not just presence of property)
      const configJson = version?.configJson?.trim();
      const config = version?.config?.trim();

      if (!configJson && !config) {
        logger.warn('[Flow] Empty recipe config detected:', {
          version: version?.version,
          transId,
          appId,
        });
        const emptyError = new Error('EMPTY_RECIPE_CONFIG');
        emptyError.name = 'EmptyRecipeError';
        throw emptyError;
      }

      const configData = configJson || config || '{}';

      // Parse the config data and extract recipe - handle nested structure
      let parsedConfig;
      try {
        parsedConfig = JSON.parse(configData);
      } catch (parseError) {
        logger.error('[Flow] JSON Parse Error:', parseError);
        logger.error('[Flow] Invalid config data (first 200 chars):', configData.substring(0, 200));
        const jsonError = new Error('INVALID_JSON_FORMAT');
        jsonError.name = 'JSONParseError';
        throw jsonError;
      }

      // Check if parsed config has any meaningful content
      const re = parsedConfig.recipe || parsedConfig;
      if (!re || (typeof re === 'object' && Object.keys(re).length === 0)) {
        logger.warn('[Flow] Parsed recipe is empty or invalid:', re);
        const emptyError = new Error('EMPTY_RECIPE_CONFIG');
        emptyError.name = 'EmptyRecipeError';
        throw emptyError;
      }
      sessionStorage.setItem('CURRENT_RECIPE_FROM_NETWORK', JSON.stringify(re));
      updateRecipe(re);
      const { nodes: initialNodes, edges: initialEdges } = generateNodesAndEdges(re);

      applyNodesPositioning(initialNodes, initialEdges)
        .then((result: { nodes: Node[]; edges: Edge[] }) => {
          if (result && result.nodes && result.edges) {
            setNodes([...result.nodes]);
            setEdges([...result.edges]);
            dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });

            // Initialize sessionStorage with loaded recipe (for save operations)
            const { nodes } = result;
            const { edges } = result;
            const updatedRecipe = updateRecipeFromNodesAndEdges(nodes, edges, re);
            sessionStorage.setItem('UPDATED_RECIPE', JSON.stringify(updatedRecipe));
          }
        })
        .catch((error: unknown) => {
          logger.error('[Flow] Error applying layout:', error);
        })
        .finally(() => {
          // Clear loading flag after nodes/edges are applied
          isLoadingRecipe.current = false;
        });

      // Update refs to track what we've loaded
      lastLoadedTransId.current = transId || null;
      lastLoadedVersion.current = String(version.version);
      isInitialLoad.current = false;

      // Update context with current version if changed
      const versionStr = String(version.version);
      if (state.currentVersion !== Number(versionStr)) {
        dispatch({ type: 'SET_CURRENT_VERSION', payload: Number(versionStr) });
      }
    } catch (e) {
      const error = e as Error;
      logger.error('[Flow] Error loading recipe:', {
        error: e,
        errorName: error.name,
        errorMessage: error.message,
        transId,
        appId,
        requestedVersion: state.currentVersion,
        availableVersions: versions?.length || 0,
      });
      dispatch({ type: 'SET_ERROR', payload: true });
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: error.message || 'UNKNOWN_ERROR' });
    } finally {
      isLoading.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Missing deps intentionally stable from context/hooks
  }, [versions, transId]); // Trigger when versions data loads

  // Handle version changes from context
  useEffect(() => {
    const currentVersionStr = state.currentVersion ? String(state.currentVersion) : null;
    const lastLoadedVersionStr = lastLoadedVersion.current
      ? String(lastLoadedVersion.current)
      : null;

    if (
      !versions ||
      versions.length === 0 ||
      !currentVersionStr ||
      lastLoadedVersionStr === currentVersionStr ||
      isInitialLoad.current ||
      isLoading.current
    ) {
      return;
    }

    try {
      isLoading.current = true;
      isLoadingRecipe.current = true; // Set flag to prevent change events during version load
      // Use string comparison for version lookup
      const version = versions.find((v) => String(v.version) === currentVersionStr);

      if (!version) {
        // Version not in local cache yet — this can happen when a new version
        // was just created and the versions list hasn't refreshed yet.
        // Return early instead of throwing; the effect will re-run once
        // getTransformerVersions refetches and includes the new version.
        logger.warn(
          '[Flow] Version not found yet:',
          currentVersionStr,
          'Available:',
          versions.map((v) => v.version),
          '— waiting for versions refresh'
        );
        isLoading.current = false;
        isLoadingRecipe.current = false;
        return;
      }

      const configJson = version?.configJson?.trim();
      const config = version?.config?.trim();

      if (!configJson && !config) {
        logger.warn('[Flow version change] Empty recipe config detected:', {
          version: version?.version,
          transId,
          appId,
        });
        const emptyError = new Error('EMPTY_RECIPE_CONFIG');
        emptyError.name = 'EmptyRecipeError';
        throw emptyError;
      }

      const configData = configJson || config || '{}';

      // Parse the config data and extract recipe - handle nested structure
      let parsedConfig;
      try {
        parsedConfig = JSON.parse(configData);
      } catch (parseError) {
        logger.error('[Flow version change] JSON Parse Error:', parseError);
        logger.error(
          '[Flow version change] Invalid config data (first 200 chars):',
          configData.substring(0, 200)
        );
        const jsonError = new Error('INVALID_JSON_FORMAT');
        jsonError.name = 'JSONParseError';
        throw jsonError;
      }

      // Check if parsed config has any meaningful content
      const re = parsedConfig.recipe || parsedConfig;
      if (!re || (typeof re === 'object' && Object.keys(re).length === 0)) {
        logger.warn('[Flow version change] Parsed recipe is empty or invalid:', re);
        const emptyError = new Error('EMPTY_RECIPE_CONFIG');
        emptyError.name = 'EmptyRecipeError';
        throw emptyError;
      }
      sessionStorage.setItem('CURRENT_RECIPE_FROM_NETWORK', JSON.stringify(re));
      updateRecipe(re);
      const { nodes: initialNodes, edges: initialEdges } = generateNodesAndEdges(re);

      applyNodesPositioning(initialNodes, initialEdges)
        .then((result: { nodes: Node[]; edges: Edge[] }) => {
          if (result && result.nodes && result.edges) {
            setNodes([...result.nodes]);
            setEdges([...result.edges]);
            dispatch({ type: 'INCREMENT_REACT_FLOW_KEY' });

            // Initialize sessionStorage with loaded recipe (for save operations)
            const { nodes } = result;
            const { edges } = result;
            const updatedRecipe = updateRecipeFromNodesAndEdges(nodes, edges, re);
            sessionStorage.setItem('UPDATED_RECIPE', JSON.stringify(updatedRecipe));
          }
        })
        .catch((error: unknown) => {
          logger.error('[Flow version change] Error applying layout:', error);
        })
        .finally(() => {
          // Clear loading flag after nodes/edges are applied
          isLoadingRecipe.current = false;
        });

      // Update ref to track the new version
      lastLoadedVersion.current = String(version.version);
    } catch (e) {
      const error = e as Error;
      logger.error('[Flow] Error loading recipe for version change:', {
        error: e,
        errorName: error.name,
        errorMessage: error.message,
        transId,
        appId,
        requestedVersion: state.currentVersion,
        availableVersions: versions?.length || 0,
      });
      dispatch({ type: 'SET_ERROR', payload: true });
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: error.message || 'UNKNOWN_ERROR' });
    } finally {
      isLoading.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- appId and transId intentionally excluded, dependencies are stable from ReactFlow hooks
  }, [
    versions,
    state.currentVersion,
    dispatch,
    updateRecipe,
    generateNodesAndEdges,
    applyNodesPositioning,
    setNodes,
    setEdges,
    getNodes,
    getEdges,
  ]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Use isModified from context instead of comparing recipes
      if (state.isModified) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state.isModified]);

  const handleSidebarCollapseChange = useCallback(
    (collapsed: boolean) => {
      dispatch({ type: 'SET_IS_SIDEBAR_COLLAPSED', payload: collapsed });
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    },
    [dispatch]
  );

  useEffect(() => {
    if (nodes.length > 0) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 350);
    }
  }, [isSidebarCollapsed, nodes.length]);

  if (error) {
    const errorMessage = state.errorMessage || '';
    const isEmptyConfig = errorMessage === 'EMPTY_RECIPE_CONFIG';

    return (
      <StateWrapper>
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {isEmptyConfig ? 'Recipe configuration is empty' : 'Error loading recipe'}
          </Typography>
          <Typography variant="body2">
            {isEmptyConfig
              ? "Can't show ReactFlow diagram because the recipe configuration is empty."
              : 'The recipe configuration could not be loaded. Check the console for details.'}
          </Typography>
        </Alert>
      </StateWrapper>
    );
  }

  if (versionsError) {
    return (
      <StateWrapper>
        <Alert severity="error">
          Error fetching recipe versions. Please check your connection and try again.
        </Alert>
      </StateWrapper>
    );
  }

  if (isLoadingVersions) {
    return (
      <StateWrapper>
        <Typography>Loading transformer versions...</Typography>
      </StateWrapper>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <StateWrapper>
        <Alert severity="warning">
          No versions found for this recipe. Please check if the recipe exists.
        </Alert>
      </StateWrapper>
    );
  }

  if (Object.keys(recipe).length === 0) {
    return (
      <StateWrapper>
        <Typography>Initializing recipe editor...</Typography>
      </StateWrapper>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: isSidebarCollapsed ? 40 : 250,
          minWidth: isSidebarCollapsed ? 40 : 250,
          pointerEvents: isReadOnly ? 'none' : 'auto',
          transition: 'width 0.3s',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Sidebar onCollapseChange={handleSidebarCollapseChange} />
      </Box>
      <Box sx={{ flex: 1, height: '100%', minHeight: 0, minWidth: 0, position: 'relative' }}>
        <ReactFlow
          key={state.reactFlowKey}
          nodes={nodes}
          edges={edges}
          onNodesChange={handleOnNodesChange}
          onEdgesChange={handleOnEdgesChange}
          onConnect={handleOnConnect}
          nodeTypes={nodeTypesToComponentMapping}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onReconnectStart={onReconnectStart}
          onConnectStart={onConnectStart}
          onReconnect={onReconnect}
          onConnectEnd={onConnectEnd}
          onReconnectEnd={onReconnectEnd}
          onEdgesDelete={onEdgesDelete}
          isValidConnection={isValidConnection(nodes, edges, Constraints, showSnackbar)}
          fitView
          fitViewOptions={{ duration: 300 }}
          minZoom={0.1}
          edgesFocusable={!isReadOnly}
          nodesDraggable={!isReadOnly}
          nodesConnectable={!isReadOnly}
          edgesReconnectable={!isReadOnly}
        >
          <Controls />
          <MiniMap />

          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          <Panel position="top-right">
            {!isReadOnly ? (
              <Button variant="outlined" onClick={handlePrettify}>
                Auto-Format
              </Button>
            ) : (
              <Typography variant="body2">
                {isReadOnly ? 'This flow is read-only' : 'This flow is editable'}
              </Typography>
            )}
          </Panel>
          <CustomAdvancedPanel isReadOnly={isReadOnly} />
        </ReactFlow>
      </Box>
    </Box>
  );
};

const FlowWithProvider = () => {
  return (
    <ErrorBoundary
      FallbackComponent={FallbackRender}
      onError={(error) => {
        recordRumError(error);
      }}
    >
      <SnackbarProvider>
        <ReactFlowProvider>
          <DnDProvider>
            <RecipeProvider>
              <CustomReactFlowStateProvider>
                <NodeNamesProvider>
                  <NodeConstraintsProvider>
                    <NodeGenerationProvider>
                      <Flow />
                    </NodeGenerationProvider>
                  </NodeConstraintsProvider>
                </NodeNamesProvider>
              </CustomReactFlowStateProvider>
            </RecipeProvider>
          </DnDProvider>
        </ReactFlowProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  );
};

export default FlowWithProvider;
